import type { QueryClient } from "@tanstack/react-query";
import type { AppWsEvent } from "../api/appEvents.ts";
import { devicesQueryKey, updateCachedDevice } from "../devices/devices.ts";
import { feedQueryKey, updateCachedMessage } from "../messages/messages.ts";

/**
 * Перезагрузить все серверные данные. Уже идущий запрос не перезапускается: после переподключения
 * сразу за собственной перезагрузкой приходит `sync_required` от backend, и второй раз грузить то же незачем.
 */
export function resyncAll(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({}, { cancelRefetch: false });
}

/**
 * Событие App WS → кэш TanStack Query. То, что событие несёт целиком (статус доставки, online-статус E-va),
 * правится на месте без запроса; остальное инвалидируется и перезагружается с backend.
 */
export function applyAppEvent(queryClient: QueryClient, event: AppWsEvent): void {
  const invalidate = (queryKey: readonly unknown[]) => void queryClient.invalidateQueries({ queryKey });
  switch (event.event) {
    case "message.status_changed":
    case "message.replayed": {
      const { deliveryStatus } = event.data;
      updateCachedMessage(queryClient, event.data.messageId, (message) => ({ ...message, deliveryStatus }));
      return;
    }
    case "message.reaction_changed": {
      const { likeCount } = event.data;
      updateCachedMessage(queryClient, event.data.messageId, (message) => ({ ...message, likeCount }));
      return;
    }
    case "message.pinned_changed": {
      // Закреплённые идут первыми и составляют отдельный фильтр: порядок и состав лент задаёт backend.
      const { pinned } = event.data;
      updateCachedMessage(queryClient, event.data.messageId, (message) => ({ ...message, pinned }));
      invalidate(feedQueryKey);
      return;
    }
    case "message.deleted":
      updateCachedMessage(queryClient, event.data.messageId, () => null);
      invalidate(feedQueryKey);
      return;
    case "message.created":
      invalidate(["statistics"]);
      invalidate(feedQueryKey);
      return;
    case "media.processed":
      invalidate(feedQueryKey);
      return;
    case "device.status_changed": {
      const { onlineStatus, lastSeenAt } = event.data;
      updateCachedDevice(queryClient, event.data.deviceId, (device) => ({
        ...device,
        onlineStatus,
        lastSeenAt: lastSeenAt ?? device.lastSeenAt,
      }));
      return;
    }
    case "device.queue_changed":
      if (event.data.messageId) {
        updateCachedMessage(queryClient, event.data.messageId, message => ({ ...message, queueStatus: event.data.status }));
      }
      invalidate(["devices", event.data.deviceId, "runtime"]);
      return;
    case "invite.accepted":
      // Новый гость E-va владельца: данные E-va и её гостей лежат под ключами `devices…`.
      invalidate(["statistics"]);
      invalidate(devicesQueryKey);
      return;
    case "guest.status_changed":
      // Гостя заблокировали, разблокировали или удалили: меняются его список E-va и лента.
      invalidate(["statistics"]);
      invalidate(devicesQueryKey);
      invalidate(feedQueryKey);
      return;
    case "sync_required":
      resyncAll(queryClient);
      return;
    default:
      event satisfies never;
  }
}
